import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter
} from "@angular/core";
import { Observable } from "rxjs/Observable";
import "rxjs/add/observable/forkJoin";

import { TranslateService } from "@ngx-translate/core";
import { State } from "@clr/angular";

import {
  SystemInfo,
  SystemInfoService,
  HelmChartVersion,
  HelmChartMaintainer
} from "./../../service/index";
import { ErrorHandler } from "./../../error-handler/error-handler";
import { toPromise, DEFAULT_PAGE_SIZE, downloadFile } from "../../utils";
import { OperationService } from "./../../operation/operation.service";
import { HelmChartService } from "./../../service/helm-chart.service";
import { ConfirmationAcknowledgement, ConfirmationDialogComponent, ConfirmationMessage } from "./../../confirmation-dialog";
import {
  OperateInfo,
  OperationState,
  operateChanges
} from "./../../operation/operate";
import {
  ConfirmationButtons,
  ConfirmationTargets,
  ConfirmationState,
  DefaultHelmIcon
} from "../../shared/shared.const";

@Component({
  selector: "hbr-helm-chart-version",
  template: `<div>
    <div class="row flex-items-xs-between">
        <div class="col-xs-4">
            <div class="title-container">
                <div class="chart-name-span">
                    {{chartName | translate}}
                </div>
                <div>
                    {{roleName | translate}}
                </div>
            </div>
        </div>
    </div>

    <div class="row version-tool">
        <div class="toolbar">
            <div class="row flex-items-xs-right option-right rightPos">
                <div class="flex-xs-middle">
                    <hbr-filter [withDivider]="true" filterPlaceholder="{{'HELM_CHART.FILTER_FOR_CHARTS' | translate}}"
                    [currentValue]="lastFilteredVersionName"
                    (filterEvt)="updateFilterValue($event)"></hbr-filter>
                    <span class="card-btn" (click)="showCard(true)" (mouseenter)="mouseEnter('card') " (mouseleave)="mouseLeave('card')">
                        <clr-icon [ngClass]="{'is-highlight': isCardView || isHovering('card') }" shape="view-cards"></clr-icon>
                    </span>
                    <span class="list-btn" (click)="showCard(false)" (mouseenter)="mouseEnter('list') " (mouseleave)="mouseLeave('list')">
                        <clr-icon [ngClass]="{'is-highlight': !isCardView || isHovering('list') }" shape="view-list"></clr-icon>
                    </span>
                    <span class="filter-divider"></span>
                    <span class="refresh-btn" (click)="refresh()">
                        <clr-icon shape="refresh"></clr-icon>
                    </span>
                </div>
            </div>
        </div>
    </div>
    <div class="row">
        <div *ngIf="!isCardView" class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <clr-datagrid (clrDgRefresh)="refresh()" [clrDgLoading]="loading" [(clrDgSelected)]="selectedRows">
                <clr-dg-action-bar>
                    <button type="button" class="btn btn-sm btn-secondary"
                    [disabled]="!(selectedRows.length===1)"
                    (click)="versionDownload()">
                        <clr-icon shape="download" size="16"></clr-icon>&nbsp;{{'HELM_CHART.DOWNLOAD' | translate}}</button>
                    <button type="button" class="btn btn-sm btn-secondary"
                    [disabled]="selectedRows.length<=0 || !hasProjectAdminRole"
                    (click)="openVersionDeleteModal(selectedRows)">
                        <clr-icon shape="times" size="16"></clr-icon>&nbsp;{{'BUTTON.DELETE' | translate}}</button>
                </clr-dg-action-bar>
                <clr-dg-column>{{'HELM_CHART.VERSION' | translate}}</clr-dg-column>
                <clr-dg-column>{{'HELM_CHART.STATUS' | translate }}</clr-dg-column>
                <clr-dg-column>{{'HELM_CHART.ENGINE' | translate }}</clr-dg-column>

                <clr-dg-column>{{'HELM_CHART.MAINTAINERS' | translate }}</clr-dg-column>
                <clr-dg-column>{{'HELM_CHART.CREATED' | translate }}</clr-dg-column>
                <clr-dg-placeholder>{{'HELM_CHART.NO_VERSION_PLACEHOLDER' | translate }}</clr-dg-placeholder>
                <clr-dg-row *ngFor="let v of chartVersions" [clrDgItem]="v">
                    <clr-dg-cell>
                        <span class="list-img">
                            <img  [src]="v.icon ?v.icon:chartDefaultIcon"  (error)="getDefaultIcon(v);" />
                        </span>
                        <a href="javascript:void(0)" (click)="onVersionClick(v)">{{ v.version }}</a>
                    </clr-dg-cell>
                    <clr-dg-cell>{{ getStatusString(v) | translate }}</clr-dg-cell>
                    <clr-dg-cell>{{ v.engine }}</clr-dg-cell>
                    <clr-dg-cell>{{ getMaintainerString(v.maintainers) }}</clr-dg-cell>
                    <clr-dg-cell>{{ v.created | date}}</clr-dg-cell>
                </clr-dg-row>
                <clr-dg-footer>
                    <clr-dg-pagination #pagination [clrDgPageSize]="pageSize" [clrDgTotalItems]="totalCount">
                        <span *ngIf="pagination.totalItems">
                            {{pagination.firstItem + 1}} - {{pagination.lastItem + 1}} {{'HELM_CHART.OF' | translate}}
                        </span>
                        {{pagination.totalItems}} {{'HELM_CHART.ITEMS'| translate}}
                    </clr-dg-pagination>
                </clr-dg-footer>
            </clr-datagrid>
        </div>
    </div>
    <div *ngIf="isCardView" class="row card-container">
        <div *ngFor="let item of chartVersions;" class="col-lg-3 col-md-4 col-sm-6">
            <a let i=index; class="card clickable" (click)="onVersionClick(item)">
                <div class="card-header">
                    <div class="card-media-block">
                        <img  [src]="item.icon ?item.icon:chartDefaultIcon"  (error)="getDefaultIcon(item);" />
                        <div class="card-media-description">
                            <span class="card-media-title">{{item.name}}</span>
                            <a class="card-media-text">{{item.home}}</a>
                        </div>
                    </div>
                </div>
                <div class="card-block">
                    <div class="form-group">
                        <label class="card-label">{{'HELM_CHART.STATUS' | translate}}</label>
                        <div>{{getStatusString(item) | translate}}</div>
                    </div>
                    <div class="form-group">
                        <label class="card-label">{{'HELM_CHART.ENGINE' | translate}}</label>
                        <div>{{item.engine}}</div>
                    </div>
                    <div class="form-group">
                        <label class="card-label">{{'HELM_CHART.MAINTAINERS' | translate}}</label>
                        <div>{{getMaintainerString(item.maintainers)}}</div>
                    </div>
                    <div class="form-group">
                        <label class="card-label">{{'HELM_CHART.VERSION' | translate}}</label>
                        <div>{{item.version}}</div>
                    </div>
                </div>
                <div class="card-footer">
                    <clr-dropdown [clrCloseMenuOnItemClick]="false">
                        <button type="button" class="btn btn-link"
                        (click)="versionDownload($event, item)">{{'HELM_CHART.DOWNLOAD' | translate}}</button>
                        <button type="button" class="btn btn-link"
                        [disabled]="selectedRows.length<=0 || !hasProjectAdminRole"
                        (click)="deleteVersionCard($event, item)">{{'BUTTON.DELETE' | translate}}</button>
                    </clr-dropdown>
                </div>
            </a>
        </div>
        <div *ngIf="loading">
            <span class="vertical-helper"></span>
            <span class="spinner"></span>
        </div>
    </div>
    <confirmation-dialog #confirmationDialog (confirmAction)="confirmDeletion($event)"></confirmation-dialog>
</div>`,
  styles: [`.title-container{display:flex}.title-container .chart-name-span{border-right:1px solid gray;font-size:27px;font-weight:400;padding-right:9px;margin-right:9px}.version-tool{position:relative}.version-tool .toolbar{overflow:hidden}.version-tool .toolbar .rightPos{position:absolute;z-index:100;right:35px;margin-top:4px}.version-tool .toolbar .rightPos .filter-divider{display:inline-block;height:16px;width:2px;background-color:#ccc;padding-top:12px;padding-bottom:12px;position:relative;top:9px;margin-right:6px;margin-left:6px}.card-container{margin-top:21px}.card-container .card-header .card-media-block img{height:45px;width:45px}.card-container .card-header .card-media-block .card-media-description{width:80%;height:45px}.card-container .card-header .card-media-block .card-media-description .card-media-title{overflow:hidden;height:24px}.card-container .card-header .card-media-block .card-media-description .card-media-text{font-size:.8em;color:#007cbb;overflow:hidden;text-overflow:ellipsis;word-wrap:break-word;white-space:nowrap;height:20px}.card-container .card-block{font-size:.9em;min-height:100px;margin-top:0}.card-container .card-block .form-group{display:flex}.card-container .card-block .form-group label{width:100px;color:#aaa}.card-container .card-footer{padding-top:6px;padding-bottom:6px}.list-img img{height:24px;width:24px;margin-right:12px}.vertical-helper{display:inline-block;height:100%;vertical-align:middle}`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartVersionComponent implements OnInit {
  signedCon: { [key: string]: any | string[] } = {};
  @Input() projectName: string;
  @Input() chartName: string;
  @Input() roleName: string;
  @Input() hasSignedIn: boolean;
  @Input() hasProjectAdminRole: boolean;
  @Input() chartDefaultIcon: string = DefaultHelmIcon;
  @Output() versionClickEvt = new EventEmitter<string>();
  @Output() backEvt = new EventEmitter<any>();


  lastFilteredVersionName: string;
  chartVersions: HelmChartVersion[] = [];
  versionsCopy: HelmChartVersion[] = [];
  systemInfo: SystemInfo;
  selectedRows: HelmChartVersion[] = [];
  loading = true;

  isCardView: boolean;
  cardHover = false;
  listHover = false;

  pageSize: number = DEFAULT_PAGE_SIZE;
  currentPage = 1;
  totalCount = 0;
  currentState: State;

  chartFile: File;
  provFile: File;

  @ViewChild("confirmationDialog")
  confirmationDialog: ConfirmationDialogComponent;

  constructor(
    private errorHandler: ErrorHandler,
    private translateService: TranslateService,
    private systemInfoService: SystemInfoService,
    private helmChartService: HelmChartService,
    private cdr: ChangeDetectorRef,
    private operationService: OperationService,
  ) {}

  public get registryUrl(): string {
    return this.systemInfo ? this.systemInfo.registry_url : "";
  }

  ngOnInit(): void {
    // Get system info for tag views
    toPromise<SystemInfo>(this.systemInfoService.getSystemInfo())
      .then(systemInfo => (this.systemInfo = systemInfo))
      .catch(error => this.errorHandler.error(error));
    this.refresh();
    this.lastFilteredVersionName = "";
  }

  updateFilterValue(value: string) {
    this.lastFilteredVersionName = value;
    this.refresh();
  }

  refresh() {
    this.loading = true;
    this.helmChartService
      .getChartVersions(this.projectName, this.chartName)
      .finally(() => {
        this.loading = false;
        let hnd = setInterval(() => this.cdr.markForCheck(), 100);
        setTimeout(() => clearInterval(hnd), 2000);
      })
      .subscribe(
        versions => {
          this.chartVersions = versions.filter(x => x.version.includes(this.lastFilteredVersionName));
          this.versionsCopy = versions.map(x => Object.assign({}, x));
          this.totalCount = versions.length;
        },
        err => {
          this.errorHandler.error(err);
        }
      );
  }

  getMaintainerString(maintainers: HelmChartMaintainer[]) {
    if (!maintainers || maintainers.length < 1) {
      return "";
    }

    let maintainer_string = maintainers[0].name;
    if (maintainers.length > 1) {
      maintainer_string = `${maintainer_string} (${maintainers.length - 1} others)`;
    }
    return maintainer_string;
  }

  onVersionClick(version: HelmChartVersion) {
    this.versionClickEvt.emit(version.version);
  }

  deleteVersion(version: HelmChartVersion): Observable<any> {
    // init operation info
    let operateMsg = new OperateInfo();
    operateMsg.name = "OPERATION.DELETE_CHART_VERSION";
    operateMsg.data.id = version.digest;
    operateMsg.state = OperationState.progressing;
    operateMsg.data.name = `${version.name}:${version.version}`;
    this.operationService.publishInfo(operateMsg);

    return this.helmChartService
      .deleteChartVersion(this.projectName, this.chartName, version.version)
      .map(
        () => operateChanges(operateMsg, OperationState.success),
        err => operateChanges(operateMsg, OperationState.failure, err)
      );
  }

  deleteVersions(versions: HelmChartVersion[]) {
    if (versions && versions.length < 1) { return; }
    let versionObs = versions.map(v => this.deleteVersion(v));
    Observable.forkJoin(versionObs).finally(() => this.refresh()).subscribe(res => {
      if (this.chartVersions.length === versionObs.length) {
        this.backEvt.emit();
      }
    });
  }

  versionDownload(evt?: Event, item?: HelmChartVersion) {
    if (evt) {
      evt.stopPropagation();
    }
    let selectedVersion: HelmChartVersion;

    if (item) {
      selectedVersion = item;
    } else {
      // return if selected version less then 1
      if (this.selectedRows.length < 1) {
        return;
      }
      selectedVersion = this.selectedRows[0];
    }
    if (!selectedVersion) {
      return;
    }

    let filename = selectedVersion.urls[0];
    this.helmChartService.downloadChart(this.projectName, filename).subscribe(
      res => {
        downloadFile(res);
      },
      error => {
        this.errorHandler.error(error);
      }
    );
  }

  showCard(cardView: boolean) {
    if (this.isCardView === cardView) {
      return;
    }
    this.isCardView = cardView;
  }

  mouseEnter(itemName: string) {
    if (itemName === "card") {
      this.cardHover = true;
    } else {
      this.listHover = true;
    }
  }

  mouseLeave(itemName: string) {
    if (itemName === "card") {
      this.cardHover = false;
    } else {
      this.listHover = false;
    }
  }

  isHovering(itemName: string) {
    if (itemName === "card") {
      return this.cardHover;
    } else {
      return this.listHover;
    }
  }

  onChartFileChangeEvent(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.chartFile = event.target.files[0];
    }
  }
  onProvFileChangeEvent(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.provFile = event.target.files[0];
    }
  }

  deleteVersionCard(env: Event, version: HelmChartVersion) {
    env.stopPropagation();
    this.openVersionDeleteModal([version]);
  }

  openVersionDeleteModal(versions: HelmChartVersion[]) {
    let versionNames = versions.map(v => v.version).join(",");
    let message = new ConfirmationMessage(
      "HELM_CHART.DELETE_CHART_VERSION_TITLE",
      "HELM_CHART.DELETE_CHART_VERSION",
      versionNames,
      versions,
      ConfirmationTargets.HELM_CHART,
      ConfirmationButtons.DELETE_CANCEL
    );
    this.confirmationDialog.open(message);
    let hnd = setInterval(() => this.cdr.markForCheck(), 100);
    setTimeout(() => clearInterval(hnd), 2000);
  }

  confirmDeletion(message: ConfirmationAcknowledgement) {
    if (
      message &&
      message.source === ConfirmationTargets.HELM_CHART &&
      message.state === ConfirmationState.CONFIRMED
    ) {
      let versions = message.data;
      this.deleteVersions(versions);
    }
  }

  getImgLink(v: HelmChartVersion) {
    if (v.icon) {
      return v.icon;
    } else {
      return DefaultHelmIcon;
    }
  }

  getDefaultIcon(v: HelmChartVersion) {
    v.icon = this.chartDefaultIcon;
  }

  getStatusString(chartVersion: HelmChartVersion) {
    if (chartVersion.deprecated) {
      return "HELM_CHART.DEPRECATED";
    } else {
      return "HELM_CHART.ACTIVE";
    }
  }
}
